/**
 * What a Matrix tile asks the server for — the one place that decides it.
 *
 * Grid numbers mirror MATRIX_FEED_FPS / MATRIX_FEED_QUALITY in
 * backend/feed_quality.py. Measured on the demo box at 640x360: q75 is
 * 28.4 KB/frame, so 30fps is 6.5 Mbit/s PER TILE and eight tiles saturate an
 * ordinary connection long before the box runs out of CPU. q55 at 12fps is
 * ~1.8 Mbit/s. The focused cell is rendered larger, so it keeps more of both —
 * the others are awareness, not inspection.
 */
export const GRID_FEED_FPS = 12;
export const GRID_FEED_QUALITY = 55;
export const GRID_PRIMARY_FEED_FPS = 20;
export const GRID_PRIMARY_FEED_QUALITY = 70;

export interface TileFeed {
  /** Hand the tile over to the per-feed LiveKit publisher when one is available. */
  preferWebrtc: boolean;
  /** Undefined means the server's default (30 fps). */
  feedFps?: number;
  /** Undefined means the compositor's own encode. */
  feedQuality?: number;
}

/**
 * A Matrix showing exactly ONE tile is a full-size view, not a grid, so it is
 * served the way the side panel is: plain MJPEG at the server defaults.
 *
 * The grid path is a bandwidth trade that a single tile does not need, and it
 * costs smoothness: the per-feed publisher samples the composited cache on its
 * own unsynchronised 15 Hz clock and drops the feed's compositor to the WebRTC
 * pull rate, which judders, and adds ~110 ms over MJPEG. Two or more tiles keep
 * the grid behaviour unchanged.
 */
export function tileFeed(tileCount: number, isPrimary: boolean): TileFeed {
  if (tileCount === 1) return { preferWebrtc: false };
  return isPrimary
    ? { preferWebrtc: true, feedFps: GRID_PRIMARY_FEED_FPS, feedQuality: GRID_PRIMARY_FEED_QUALITY }
    : { preferWebrtc: true, feedFps: GRID_FEED_FPS, feedQuality: GRID_FEED_QUALITY };
}
